import React, { useRef, useEffect } from 'react';
import { BiHistory } from 'react-icons/bi';
import { ChatItem } from '../types';
import ChatHistoryItem from './ChatHistoryItem';

interface ChatHistoryListProps {
  chats: ChatItem[];
  unreadChatIds?: Set<string>;
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  selectedChatId?: string | null;
  onChatSelect: (chatId: string) => void;
  onChatRename: (chat: ChatItem) => void;
  onChatDeleteRequest: (chat: ChatItem) => void;
  onLoadMore?: () => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

const ChatHistoryList: React.FC<ChatHistoryListProps> = ({
  chats,
  unreadChatIds,
  isLoading,
  isLoadingMore = false,
  hasMore = false,
  selectedChatId,
  onChatSelect,
  onChatRename,
  onChatDeleteRequest,
  onLoadMore,
  scrollContainerRef,
}) => {
  const bottomObserverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!bottomObserverRef.current || !onLoadMore || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      {
        root: scrollContainerRef?.current,
        rootMargin: '50px',
        threshold: 0.1,
      }
    );

    observer.observe(bottomObserverRef.current);

    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isLoadingMore, scrollContainerRef]);

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600">
        <BiHistory className="w-4 h-4" />
        <span>Chat History</span>
      </div>
      <div className="mt-2 space-y-1">
        {isLoading ? (
          <div className="px-3 py-2 text-sm text-gray-500 text-center">
            Loading...
          </div>
        ) : chats.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-500 text-center">
            No chat history yet
          </div>
        ) : (
          <>
            {chats.map((chat) => (
              <ChatHistoryItem
                key={chat.chat_id}
                chat={chat}
                isSelected={selectedChatId === chat.chat_id}
                hasUnread={Boolean(unreadChatIds?.has(chat.chat_id))}
                onSelect={onChatSelect}
                onRename={onChatRename}
                onDelete={onChatDeleteRequest}
              />
            ))}

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                Loading more...
              </div>
            )}

            {/* No more chats message */}
            {!hasMore && chats.length > 0 && (
              <div className="px-3 py-2 text-sm text-gray-400 text-center">
                No more chats
              </div>
            )}

            {/* Intersection observer target */}
            <div ref={bottomObserverRef} className="h-1" />
          </>
        )}
      </div>
    </>
  );
};

export default ChatHistoryList;

